<script setup lang="ts">
  import {
    Avatar,
    AvatarFallback,
    AvatarImage,
    cn,
    useAppLoading
  } from '@gat-ship/vue-components'
  import {
    IconAdmin,
    IconBan,
    IconCalendarClock,
    IconLoading
  } from '@gat-ship/icons'
  import { type HTMLAttributes, computed } from 'vue'
  import { type TenantUser } from '@gat-ship/types'
  import { useTenantUserUtils } from '../../composables/useTenantUserUtils'

  const props = defineProps<{
    tenantUser: TenantUser
    showIcons?: boolean
    class?: HTMLAttributes['class']
    loadingId?: string
  }>()

  const { TenantUserUtils } = useTenantUserUtils()

  const userStates = computed(() => {
    return {
      isActive: TenantUserUtils.isActiveTenantUser(props.tenantUser),
      isScheduledForDeactivation: TenantUserUtils.isScheduledForDeactivation(
        props.tenantUser
      ),
      isScheduledForActivation: TenantUserUtils.isScheduledForActivation(
        props.tenantUser
      ),
      daysToDeactivation: TenantUserUtils.isScheduledForDeactivation(
        props.tenantUser
      )
    }
  })

  const showIcon = computed(() => {
    return (
      props.showIcons &&
      (userStates.value.isScheduledForDeactivation ||
        userStates.value.isScheduledForActivation ||
        !userStates.value.isActive ||
        props.tenantUser.isTenantAdmin)
    )
  })

  const { isLoading } = useAppLoading()

  function getInitials(fullName: string) {
    return fullName
      .split(' ')
      .map((name) => name[0])
      .join('')
  }
</script>

<template>
  <div class="relative flex items-center justify-center p-1">
    <Avatar :class="cn('relative size-10 rounded-full', props.class)">
      <div
        :data-loading="isLoading(props.loadingId || props.tenantUser.userId)"
        class="absolute inset-0 flex hidden aspect-square size-10 items-center justify-center bg-black/50 duration-100 data-[loading=true]:flex data-[loading=true]:animate-in data-[loading=false]:animate-out data-[loading=false]:fade-out-0 data-[loading=true]:fade-in-0"
      >
        <IconLoading class="size-4 text-white" />
      </div>
      <AvatarImage
        v-if="props.tenantUser.imageUrl"
        :src="props.tenantUser.imageUrl"
      />
      <AvatarFallback>{{
        getInitials(props.tenantUser.fullName)
      }}</AvatarFallback>
    </Avatar>
    <div
      v-if="showIcon"
      class="absolute left-0 top-0 flex size-6 items-center justify-center rounded-full p-1 before:absolute before:inset-0 before:z-[2] before:rounded-full before:bg-black/50 before:p-1 before:content-['']"
    >
      <icon-calendar-clock
        v-if="userStates.isScheduledForDeactivation"
        class="z-[3] text-orange-500"
      />
      <icon-calendar-clock
        v-else-if="userStates.isScheduledForActivation"
        class="z-[3] text-green-500"
      />
      <icon-ban
        v-else-if="!userStates.isActive"
        class="text-destructive z-[3]"
      />
      <icon-admin
        v-else-if="props.tenantUser.isTenantAdmin"
        class="z-[3] text-yellow-500"
      />
    </div>
  </div>
</template>
